import { useEffect, useMemo, useState } from 'react'
import { useBottomSheet } from '@/context/bottom-sheet-context'
import { useNavigation } from '@/app/hooks'
import { useAppDispatch, useAppSelector, useMockableViewModel } from '@/hooks'
import { UtilityProgram } from '@/types/utilityProgram'
import {
  programsNewCollection,
  selectEnrolledProgram,
} from '@/reducers/programsNew'
import { useCurrentMultiProgramFlowContext } from '@/app/context/MultiProgram/MultiProgramFlowContexts'
import { searchableUtilityProgramsCollection } from '@/reducers/utilityPrograms'
import { useParams } from 'react-router-dom'
import { programRequirementsSheetConfig } from '@/app/components/ProgramRequirementsSheet/ProgramRequirementsSheet'
import { startUnenrollment } from '@/reducers/inProgressUtilityProgramUnenrollments'
import useUtilityProgramRewards from '@/app/hooks/useUtilityProgramRewards'
import useEnrollableDevices from '@/app/hooks/useEnrollableDevices'
import { useHasAtLeastOneDeviceEnrollment } from '@/app/hooks/useHasAtLeastOneDeviceEnrollment'
import { invalidateCollections } from '@/app/pages/multi-program-enrollment/utils/utils'
import getProgramEnrollmentExpiration from '@/utils/utility-program-helpers/getProgramEnrollmentExpiration'

function useViewModel() {
  const dispatch = useAppDispatch()
  const { id: utilityProgramIdParam } = useParams<{ id: string }>()
  const utilityProgramId = parseInt(utilityProgramIdParam)
  const { data: utilityPrograms } =
    searchableUtilityProgramsCollection.useFetch({
      path: `utility_programs/${utilityProgramId}`,
    })

  const [displayEnrollSuccess, setDisplayEnrollSuccess] =
    useState<boolean>(false)

  const utilityProgram = utilityPrograms?.[0] as UtilityProgram | undefined
  const { isLoading: isProgramsLoading } = programsNewCollection.useFetch()
  const { rewardsByCategory } = useUtilityProgramRewards(utilityProgramId)
  const navigate = useNavigation()

  const { enrollableDeviceCount } = useEnrollableDevices(utilityProgramId)

  const enrolledProgram = useAppSelector(
    selectEnrolledProgram(utilityProgramId),
  )
  const { isExpired } = getProgramEnrollmentExpiration(
    enrolledProgram?.utility_program?.preferred_display_timezone,
    enrolledProgram?.view_config_json?.programCard?.enrollmentEndDate,
    enrolledProgram?.view_config_json?.programCard?.rollingDeadline ?? false,
  )

  const { flow, onExitFlow } = useCurrentMultiProgramFlowContext()

  const programContent = useMemo(() => {
    if (!utilityProgram) {
      return null
    }
    return {
      name: utilityProgram.view_config_json?.name ?? utilityProgram.name,
      subtitle:
        utilityProgram.view_config_json.levelThreeConfig?.levelThreeSubtitle,
      summary:
        utilityProgram.view_config_json.levelThreeConfig?.levelThreeSummary,
      logoUrl: utilityProgram.view_config_json.logoUrl,
      getValueChips: (
        utilityProgram.view_config_json.levelThreeConfig?.valueChips ?? []
      )
        .filter((c) => c?.valueChipKind && c?.valueChipText)
        .filter((c) => !c?.isGivingValue)
        .map((c) => ({
          kind: c?.valueChipKind ?? '',
          text: c?.valueChipText ?? '',
        })),
      giveValueChips: (
        utilityProgram.view_config_json.levelThreeConfig?.valueChips ?? []
      )
        .filter((c) => c?.valueChipKind && c?.valueChipText)
        .filter((c) => c?.isGivingValue)
        .map((c) => ({
          kind: c?.valueChipKind ?? '',
          text: c?.valueChipText ?? '',
        })),
    }
  }, [utilityProgram])
  const { closeBottomSheet, config, snapToHeader, setBottomSheetContent } =
    useBottomSheet()
  const onBack = () => {
    closeBottomSheet()
    onExitFlow()
  }

  useEffect(
    function openBottomSheet() {
      if (!utilityProgram) {
        return
      }
      if (isProgramsLoading) {
        return
      }
      if (enrolledProgram) {
        return
      }
      if (isExpired) {
        return
      }
      if (config) {
        snapToHeader()
        return
      }
      setBottomSheetContent(
        programRequirementsSheetConfig({ utilityProgram }, flow),
      )
      snapToHeader()
    },
    [config, utilityProgram, isProgramsLoading, enrolledProgram],
  )

  const onUnenrollClick = () => {
    dispatch(startUnenrollment(utilityProgram?.id ?? 0))
    navigate.push(
      `/utility-program/${utilityProgram?.id ?? 0}/unenroll/device-select`,
    )
  }

  const onSuccessfulQuickEnroll = () => {
    setDisplayEnrollSuccess(true)
    invalidateCollections(dispatch)
    closeBottomSheet()
  }

  const displayEnrollAdditional =
    enrollableDeviceCount > 0 &&
    Boolean(enrolledProgram) &&
    !isExpired &&
    utilityProgram?.active

  const hasAtLeastOneDeviceEnrollment =
    useHasAtLeastOneDeviceEnrollment(enrolledProgram)

  return {
    utilityProgram,
    programContent,
    onBack,
    isEnrolled:
      !!enrolledProgram && !isExpired && hasAtLeastOneDeviceEnrollment,
    onUnenrollClick,
    closeBottomSheet,
    rewardsByCategory,
    utilityProgramId,
    displayEnrollAdditional,
    onSuccessfulQuickEnroll,
    displayEnrollSuccess,
  }
}

const useMockViewModel = () => {
  return {
    utilityProgram: {} as UtilityProgram,
    programContent: {
      name: 'Energy Savings Program',
      subtitle: 'Save energy and reduce your bill',
      tagLine: {
        get: 'Get rewards for energy efficiency',
        give: 'Help the environment by reducing usage',
      },
      summary:
        'Join our Energy Savings Program to reduce your monthly bills while helping the environment. Earn rewards for cutting back usage during peak hours.',
      logoUrl: 'https://example.com/energy-program-logo.png',
      getValueChips: [
        { kind: 'savings', text: 'Save up to $30/month' },
        { kind: 'reward', text: 'Earn points for each kWh saved' },
      ],
      giveValueChips: [
        { kind: 'environmental', text: 'Reduce carbon footprint' },
        { kind: 'community', text: 'Support grid reliability' },
      ],
    },
    onBack: () => {},
    isEnrolled: false,
    onUnenrollClick: () => {},
    closeBottomSheet: () => {},
    rewardsByCategory: null,
    utilityProgramId: 1,
    displayEnrollAdditional: false,
    onSuccessfulQuickEnroll: () => {},
    displayEnrollSuccess: false,
  }
}

export default useMockableViewModel({ useViewModel, useMockViewModel })
